import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  SongEditorActionRow,
  SongEditorAlbumCardButton,
  SongEditorAlbumCardState,
  SongEditorAlbumCardTitle,
  SongEditorAlbumList,
  SongEditorAlbumSection,
  SongEditorAlbumSectionHeader,
  SongEditorAlbumSectionTitle,
  SongEditorCheckbox,
  SongEditorCheckboxLabel,
  SongEditorDangerButton,
  SongEditorFieldGrid,
  SongEditorFieldGroup,
  SongEditorFieldLabel,
  SongEditorFormCard,
  SongEditorHelperText,
  SongEditorIncrementButton,
  SongEditorLayout,
  SongEditorPageActions,
  SongEditorPageCard,
  SongEditorPageGrid,
  SongEditorPageIndex,
  SongEditorPagePreview,
  SongEditorPageRemoveButton,
  SongEditorPlayedCountRow,
  SongEditorPlayedCountValue,
  SongEditorPrimaryButton,
  SongEditorSecondaryLink,
  SongEditorSectionBody,
  SongEditorSectionHeader,
  SongEditorSectionTitle,
  SongEditorStatusText,
  SongEditorTextInput,
  SongEditorUploadInput,
} from '../components/styled';
import { useLibraryApi } from '../context/BandContext';
import { api } from '../lib/api';
import { fileToDataUrl } from '../lib/fileToDataUrl';

function SongEditorPage() {
  const navigate = useNavigate();
  const { songId = '', bandId = '' } = useParams();
  const libraryApi = useLibraryApi();
  const pathPrefix = bandId ? `/bands/${bandId}` : '';
  const [searchParams] = useSearchParams();
  const isEditing = Boolean(songId);
  const albumId = searchParams.get('albumId') || '';
  const sourceQuery = searchParams.get('q') || '';
  const sourceAlphaRange = searchParams.get('alpha') || '';
  const sourceSortBy = searchParams.get('sort') || '';
  const songSearchParams = new URLSearchParams();

  if (albumId) {
    songSearchParams.set('albumId', albumId);
  }

  if (sourceQuery) {
    songSearchParams.set('q', sourceQuery);
  }

  if (sourceAlphaRange) {
    songSearchParams.set('alpha', sourceAlphaRange);
  }

  if (sourceSortBy) {
    songSearchParams.set('sort', sourceSortBy);
  }

  const songSearch = songSearchParams.toString()
    ? `?${songSearchParams.toString()}`
    : '';
  const librarySearchParams = new URLSearchParams();

  if (sourceQuery) {
    librarySearchParams.set('q', sourceQuery);
  }

  if (sourceAlphaRange) {
    librarySearchParams.set('alpha', sourceAlphaRange);
  }

  if (sourceSortBy) {
    librarySearchParams.set('sort', sourceSortBy);
  }

  if (bandId) {
    librarySearchParams.set('scope', 'band');
    librarySearchParams.set('bandId', bandId);
  }

  const librarySearch = librarySearchParams.toString()
    ? `?${librarySearchParams.toString()}`
    : '';
  const libraryBackTo = albumId
    ? `/library/albums/${albumId}${librarySearch}`
    : `/library/songs${librarySearch}`;
  const [formState, setFormState] = useState({
    title: '',
    artist: '',
    pages: [],
    playedCount: 0,
    flag: false,
  });
  const [albums, setAlbums] = useState([]);
  const [bands, setBands] = useState([]);
  const [selectedBandIds, setSelectedBandIds] = useState([]);
  const [selectedAlbumIds, setSelectedAlbumIds] = useState([]);
  const [albumQuery, setAlbumQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingAlbumActionKey, setPendingAlbumActionKey] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isActive = true;

    const loadData = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const [albumsResponse, bandsResponse, songResponse] = await Promise.all([
          libraryApi.getAlbums(),
          api.getBands(),
          isEditing ? libraryApi.getSong(songId) : Promise.resolve({ song: null }),
        ]);

        if (!isActive) {
          return;
        }

        const nextAlbums = [...(albumsResponse?.albums || [])].sort(
          (leftAlbum, rightAlbum) =>
            (leftAlbum.title || '').localeCompare(rightAlbum.title || '')
        );

        setAlbums(nextAlbums);
        setBands((bandsResponse?.bands || []).sort((left, right) => (left.name || '').localeCompare(right.name || '')));

        if (isEditing) {
          const song = songResponse?.song;

          setFormState({
            title: song?.title || '',
            artist: song?.artist || '',
            pages: Array.isArray(song?.pages) ? song.pages : [],
            playedCount:
              Number.isInteger(song?.playedCount) && song.playedCount >= 0
                ? song.playedCount
                : 0,
            flag: Boolean(song?.flag),
          });

          setSelectedAlbumIds(
            nextAlbums
              .filter((album) =>
                (album.songs || []).some((currentSong) => currentSong._id === songId)
              )
              .map((album) => album._id)
          );
          setSelectedBandIds([...new Set([
            ...(song?.bandId ? [song.bandId] : []),
            ...(song?.sharedBandIds || []),
          ].map((id) => id.toString()))]);
        } else {
          setFormState({
            title: '',
            artist: '',
            pages: [],
            playedCount: 0,
            flag: false,
          });
          setSelectedAlbumIds([]);
          setSelectedBandIds(bandId ? [bandId] : []);
        }
      } catch (error) {
        if (isActive && error.status !== 401) {
          setErrorMessage(error.message || 'Failed to load song data.');
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isActive = false;
    };
  }, [isEditing, songId, libraryApi, bandId]);

  const handleFieldChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormState((currentState) => ({
      ...currentState,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) {
      return;
    }

    setErrorMessage('');

    try {
      const pageStrings = await Promise.all(files.map(fileToDataUrl));

      setFormState((currentState) => ({
        ...currentState,
        pages: [...currentState.pages, ...pageStrings],
      }));
    } catch {
      setErrorMessage('Failed to read one or more image files.');
    } finally {
      event.target.value = '';
    }
  };

  const handleRemovePage = (pageIndex) => {
    setFormState((currentState) => ({
      ...currentState,
      pages: currentState.pages.filter((_, index) => index !== pageIndex),
    }));
  };

  const handleIncrementPlayedCount = async () => {
    const nextPlayedCount = Math.max(0, Number(formState.playedCount) || 0) + 1;

    if (!isEditing) {
      setFormState((currentState) => ({
        ...currentState,
        playedCount: nextPlayedCount,
      }));
      return;
    }

    setPendingAlbumActionKey('played-count');
    setErrorMessage('');

    try {
      await libraryApi.updateSong(songId, { playedCount: nextPlayedCount });
      setFormState((currentState) => ({
        ...currentState,
        playedCount: nextPlayedCount,
      }));
    } catch (error) {
      if (error.status !== 401) {
        setErrorMessage(error.message || 'Failed to update played count.');
      }
    } finally {
      setPendingAlbumActionKey('');
    }
  };

  const filteredAlbums = useMemo(() => {
    const trimmedQuery = albumQuery.trim().toLowerCase();

    return albums.filter((album) => {
      if (!trimmedQuery) {
        return true;
      }

      const haystack = [album.title || '', album.artist || '']
        .join(' ')
        .toLowerCase();

      return haystack.includes(trimmedQuery);
    });
  }, [albumQuery, albums]);

  const handleQueueAlbumForNewSong = (albumId) => {
    setSelectedAlbumIds((currentIds) =>
      currentIds.includes(albumId) ? currentIds : [...currentIds, albumId]
    );
  };

  const handleRemoveQueuedAlbum = (albumId) => {
    setSelectedAlbumIds((currentIds) =>
      currentIds.filter((currentId) => currentId !== albumId)
    );
  };

  const handleAddToAlbum = async (album) => {
    if (!isEditing) {
      handleQueueAlbumForNewSong(album._id);
      return;
    }

    setPendingAlbumActionKey(`add:${album._id}`);
    setErrorMessage('');

    try {
      await libraryApi.addSongToAlbum(album._id, songId);

      setSelectedAlbumIds((currentIds) =>
        currentIds.includes(album._id) ? currentIds : [...currentIds, album._id]
      );

      setAlbums((currentAlbums) =>
        currentAlbums.map((currentAlbum) =>
          currentAlbum._id === album._id
            ? {
                ...currentAlbum,
                songs: [
                  ...(currentAlbum.songs || []),
                  {
                    _id: songId,
                    title: formState.title || 'Untitled song',
                    artist: formState.artist || '',
                  },
                ],
              }
            : currentAlbum
        )
      );
    } catch (error) {
      if (error.status !== 401) {
        setErrorMessage(error.message || 'Failed to add song to album.');
      }
    } finally {
      setPendingAlbumActionKey('');
    }
  };

  const handleRemoveFromAlbum = async (album) => {
    if (!isEditing) {
      handleRemoveQueuedAlbum(album._id);
      return;
    }

    setPendingAlbumActionKey(`remove:${album._id}`);
    setErrorMessage('');

    try {
      const remainingSongIds = (album.songs || [])
        .filter((currentSong) => currentSong._id !== songId)
        .map((currentSong) => currentSong._id);

      await libraryApi.updateAlbum(album._id, {
        songs: remainingSongIds,
      });

      setSelectedAlbumIds((currentIds) =>
        currentIds.filter((currentId) => currentId !== album._id)
      );

      setAlbums((currentAlbums) =>
        currentAlbums.map((currentAlbum) =>
          currentAlbum._id === album._id
            ? {
                ...currentAlbum,
                songs: (currentAlbum.songs || []).filter(
                  (currentSong) => currentSong._id !== songId
                ),
              }
            : currentAlbum
        )
      );
    } catch (error) {
      if (error.status !== 401) {
        setErrorMessage(error.message || 'Failed to remove song from album.');
      }
    } finally {
      setPendingAlbumActionKey('');
    }
  };

  const handleToggleAlbum = (album) => {
    if (selectedAlbumIds.includes(album._id)) {
      handleRemoveFromAlbum(album);
      return;
    }

    handleAddToAlbum(album);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setErrorMessage('');

    const payload = {
      title: formState.title.trim(),
      pages: formState.pages,
      artist: formState.artist.trim() || undefined,
      playedCount: Math.max(0, Number(formState.playedCount) || 0),
      flag: formState.flag,
      sharedBandIds: selectedBandIds,
    };

    try {
      const data = isEditing
        ? await libraryApi.replaceSong(songId, payload)
        : await libraryApi.saveSong(payload);

      const nextSongId = data?.song?._id || songId;

      if (!isEditing && selectedAlbumIds.length > 0) {
        await Promise.all(
          selectedAlbumIds.map((albumId) => libraryApi.addSongToAlbum(albumId, nextSongId))
        );
      }

      navigate(`${pathPrefix}/song/${nextSongId}${songSearch}`);
    } catch (error) {
      if (error.status !== 401) {
        setErrorMessage(error.message || 'Failed to save song.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSong = async () => {
    if (
      typeof window !== 'undefined' &&
      !window.confirm(`Delete "${formState.title || 'this song'}"?`)
    ) {
      return;
    }

    setIsDeleting(true);
    setErrorMessage('');

    try {
      await libraryApi.deleteSong(songId);
      navigate(libraryBackTo);
    } catch (error) {
      if (error.status !== 401) {
        setErrorMessage(error.message || 'Failed to delete song.');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <SongEditorLayout>
      <SongEditorFormCard onSubmit={handleSubmit}>
        <SongEditorSectionHeader>
          <SongEditorSectionTitle>
            {isEditing ? 'Edit Song' : 'New Song'}
          </SongEditorSectionTitle>
          <SongEditorSectionBody>
            Update metadata and page images here. Album membership is managed
            from this screen as well.
          </SongEditorSectionBody>
        </SongEditorSectionHeader>

        {isLoading ? <SongEditorStatusText>Loading song...</SongEditorStatusText> : null}
        {errorMessage ? (
          <SongEditorStatusText $error>{errorMessage}</SongEditorStatusText>
        ) : null}

        {!isLoading ? (
          <>
            <SongEditorFieldGrid>
              <SongEditorFieldGroup>
                <SongEditorFieldLabel htmlFor="title">Title</SongEditorFieldLabel>
                <SongEditorTextInput
                  id="title"
                  name="title"
                  value={formState.title}
                  onChange={handleFieldChange}
                  placeholder="Song title"
                  required
                />
              </SongEditorFieldGroup>

              <SongEditorFieldGroup>
                <SongEditorFieldLabel htmlFor="artist">Artist</SongEditorFieldLabel>
                <SongEditorTextInput
                  id="artist"
                  name="artist"
                  value={formState.artist}
                  onChange={handleFieldChange}
                  placeholder="Artist"
                />
              </SongEditorFieldGroup>
            </SongEditorFieldGrid>

            <SongEditorFieldGroup>
              <SongEditorFieldLabel htmlFor="flag">Flag</SongEditorFieldLabel>
              <SongEditorCheckboxLabel htmlFor="flag">
                <SongEditorCheckbox
                  id="flag"
                  name="flag"
                  type="checkbox"
                  checked={formState.flag}
                  onChange={handleFieldChange}
                />
                Mark this song as flagged
              </SongEditorCheckboxLabel>
            </SongEditorFieldGroup>

            <SongEditorFieldGroup>
              <SongEditorFieldLabel>Share with bands</SongEditorFieldLabel>
              {bands.length ? bands.map((band) => (
                <SongEditorCheckboxLabel key={band._id} htmlFor={`song-band-${band._id}`}>
                  <SongEditorCheckbox
                    id={`song-band-${band._id}`}
                    type="checkbox"
                    checked={selectedBandIds.includes(band._id)}
                    onChange={() => setSelectedBandIds((ids) => ids.includes(band._id) ? ids.filter((id) => id !== band._id) : [...ids, band._id])}
                    disabled={isSaving || isDeleting || bandId === band._id}
                  />
                  {band.name}
                </SongEditorCheckboxLabel>
              )) : <SongEditorHelperText>No bands are available.</SongEditorHelperText>}
            </SongEditorFieldGroup>

            {isEditing ? (
              <SongEditorPlayedCountRow>
                <SongEditorPlayedCountValue>
                  Played count: {formState.playedCount}
                </SongEditorPlayedCountValue>
                <SongEditorIncrementButton
                  type="button"
                  onClick={handleIncrementPlayedCount}
                  disabled={
                    isSaving || isDeleting || Boolean(pendingAlbumActionKey)
                  }
                >
                  {pendingAlbumActionKey === 'played-count'
                    ? 'Updating...'
                    : 'Increment Played Count'}
                </SongEditorIncrementButton>
              </SongEditorPlayedCountRow>
            ) : null}

            <SongEditorFieldGroup>
              <SongEditorFieldLabel htmlFor="page-upload">
                Page Images
              </SongEditorFieldLabel>
              <SongEditorUploadInput
                id="page-upload"
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
              />
              <SongEditorHelperText>
                {formState.pages.length} page
                {formState.pages.length === 1 ? '' : 's'} attached
              </SongEditorHelperText>
            </SongEditorFieldGroup>

            {formState.pages.length > 0 ? (
              <SongEditorPageGrid>
                {formState.pages.map((page, index) => (
                  <SongEditorPageCard key={`page-${index}`}>
                    <SongEditorPagePreview
                      src={page}
                      alt={`${formState.title || 'Song'} page ${index + 1}`}
                    />
                    <SongEditorPageActions>
                      <SongEditorPageIndex>Page {index + 1}</SongEditorPageIndex>
                      <SongEditorPageRemoveButton
                        type="button"
                        onClick={() => handleRemovePage(index)}
                      >
                        Remove
                      </SongEditorPageRemoveButton>
                    </SongEditorPageActions>
                  </SongEditorPageCard>
                ))}
              </SongEditorPageGrid>
            ) : (
              <SongEditorStatusText>No page images added yet.</SongEditorStatusText>
            )}

            <SongEditorAlbumSection>
              <SongEditorAlbumSectionHeader>
                <SongEditorAlbumSectionTitle>Albums</SongEditorAlbumSectionTitle>
              </SongEditorAlbumSectionHeader>

              <SongEditorFieldGroup>
                <SongEditorTextInput
                  id="album-search"
                  name="album-search"
                  value={albumQuery}
                  onChange={(event) => setAlbumQuery(event.target.value)}
                  placeholder="Search albums"
                  aria-label="Search albums"
                />
              </SongEditorFieldGroup>

              {filteredAlbums.length > 0 ? (
                <SongEditorAlbumList>
                  {filteredAlbums.map((album) => {
                    const isSelected = selectedAlbumIds.includes(album._id);
                    const isPending =
                      pendingAlbumActionKey === `add:${album._id}` ||
                      pendingAlbumActionKey === `remove:${album._id}`;

                    return (
                      <SongEditorAlbumCardButton
                        key={album._id}
                        type="button"
                        onClick={() => handleToggleAlbum(album)}
                        disabled={
                          Boolean(pendingAlbumActionKey) || isSaving || isDeleting
                        }
                        $selected={isSelected}
                      >
                        <SongEditorAlbumCardTitle>
                          {album.title || 'Untitled album'}
                        </SongEditorAlbumCardTitle>
                        <SongEditorAlbumCardState $selected={isSelected}>
                          {isPending ? '...' : isSelected ? '✓' : '+'}
                        </SongEditorAlbumCardState>
                      </SongEditorAlbumCardButton>
                    );
                  })}
                </SongEditorAlbumList>
              ) : (
                <SongEditorStatusText>
                  {albums.length === 0
                    ? 'No albums are available yet.'
                    : albumQuery.trim()
                      ? 'No albums matched your search.'
                      : 'No albums are available yet.'}
                </SongEditorStatusText>
              )}

              {!isEditing ? (
                <SongEditorHelperText>
                  Selected albums will be updated immediately after the new song
                  is created.
                </SongEditorHelperText>
              ) : null}
            </SongEditorAlbumSection>

            <SongEditorActionRow>
              <SongEditorPrimaryButton
                type="submit"
                disabled={
                  isSaving ||
                  isDeleting ||
                  Boolean(pendingAlbumActionKey) ||
                  !formState.pages.length
                }
              >
                {isSaving
                  ? isEditing
                    ? 'Saving...'
                    : 'Creating...'
                  : isEditing
                    ? 'Save Song'
                    : 'Create Song'}
              </SongEditorPrimaryButton>

              <SongEditorSecondaryLink
                as={Link}
                to={isEditing ? `${pathPrefix}/song/${songId}${songSearch}` : libraryBackTo}
              >
                {isEditing ? 'Back to Song' : 'Back to Library'}
              </SongEditorSecondaryLink>

              {isEditing ? (
                <SongEditorDangerButton
                  type="button"
                  onClick={handleDeleteSong}
                  disabled={isSaving || isDeleting || Boolean(pendingAlbumActionKey)}
                >
                  {isDeleting ? 'Deleting Song...' : 'Delete Song'}
                </SongEditorDangerButton>
              ) : null}
            </SongEditorActionRow>
          </>
        ) : null}
      </SongEditorFormCard>
    </SongEditorLayout>
  );
}

export default SongEditorPage;
